import React, { Suspense } from "react";
import { ComponentNode } from "../types/template";
import { convertStylesToInline, getCustomThemeGridStyle, isResponsiveSidebar } from "../utils/styleUtils";
import TextComponent from "./TextComponent";
import HeadingComponent from "./HeadingComponent";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import CardComponent from "./CardComponent";
import ContainerComponent from "./ContainerComponent";
import ColumnComponent from "./ColumnComponent";
import BlogSectionComponent from "./BlogSectionComponent";
import LinkComponent from "./LinkComponent";
import IconComponent from "./IconComponent";
import DynamicSectionComponent from "./DynamicSectionComponent";
import OfferBannerSectionComponent from "./OfferBannerSectionComponent";
import FaqItemComponent from "./FaqItemComponent";
import SearchFormComponent from "./SearchFormComponent";
import NewsletterFormComponent from "./NewsletterFormComponent";
import ContactFormComponent from "./ContactFormComponent";
import EventDealsComponent from "./EventDealsComponent";
import HeaderComponent from "./HeaderComponent";
import FooterComponent from "./FooterComponent";
import CategorySectionComponent from "./CategorySectionComponent";
import ProductDetailTrigger from "./ProductDetailTrigger";
import { getNestedValue, hasRenderableValue } from "../utils/dataBinding";
import {
  BuilderFooterRuntime,
  BuilderHeaderRuntime,
  BuilderOffersRuntime,
} from "./BuilderPreviewRuntime";

interface ComponentRendererProps {
  component: ComponentNode;
  data?: any;
  context?: {
    companyId?: string;
    templateId?: string;
    domain?: string;
    pageSlug?: string;
    basePath?: string;
    params?: Record<string, any>;
    searchParams?: Record<string, any>;
    store_slug?: string;
    slug_type?: string;
    category_slug?: string;
    promotion_slug?: string;
    out_link?: string;
    staticPageData?: any;
  };
}

/**
 * Main component renderer that routes to specific component types
 */
const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  component,
  data,
  context,
}) => {
  const { type, styles, customClassName } = component;
  const inlineStyles = convertStylesToInline(styles);

  // An author chose to hide this part outright (unconditional, unlike
  // hideIfEmpty's data-driven check) — e.g. turning off a card's badge.
  if (component.hidden) {
    return null;
  }

  // Skip entirely rather than show a blank shell — mirrors
  // ClientComponentRenderer.tsx's pre-switch check, which this server-side
  // renderer was missing (a CONTAINER/TEXT/LINK node with hideIfEmpty was
  // silently rendered anyway).
  if (
    component.hideIfEmpty &&
    !hasRenderableValue(getNestedValue(data, component.hideIfEmpty))
  ) {
    return null;
  }

  const rowStyles =
    type === "ROW"
      ? {
          ...inlineStyles,
          "--custom-theme-column-gap":
            typeof styles?.gap === "number"
              ? `${styles.gap}px`
              : (styles?.gap ?? "0px"),
        }
      : inlineStyles;
  const layoutStyles =
    type === "GRID"
      ? { ...inlineStyles, ...getCustomThemeGridStyle(component.gridCols, component.gridGap ?? (typeof styles?.gap === "number" ? styles.gap : undefined)) }
      : rowStyles;
  const dynamicSectionTypes = new Set([
    "BlogSection",
    "HeroBanner",
    "CategorySection",
    "FaqSection",
    "MerchantSection",
    "OffersSection",
    "OfferSection",
    "EventOfferSection",
    "EventSection",
    "MerchantsAlphabetSection",
    "ProductsSection",
    "TopDealsSection",
    "PromotionSection",
    "StoreSearchSection",
    "NewsletterSection",
    "BreadcrumbSection",
    "TagsSection",
    "CustomBanner",
    "PopularDeals",
    "FooterCategories",
    "CombinedOffersSection",
    "InnerCategoriesSection",
    // Generic block types (e.g. Offers Banner, Sidebar) with no
    // dedicated ComponentType carry an apiConfig on a plain "SECTION"
    // node — without this, they silently fell through to the bare
    // `case 'SECTION'` below (one unexpanded, unbound render of the
    // repeated CARD template), never reaching DynamicSectionComponent
    // at all. Confirmed as a real pre-existing gap, not new.
    "SECTION",
  ]);
  const structuralSectionTypes = new Set([
    "Header",
    "HeroBanner",
    "CategorySection",
    "FaqSection",
    "FAQ_ITEM",
    "MerchantSection",
    "OffersSection",
    "OfferSection",
    "EventOfferSection",
    "EventSection",
    "MerchantsAlphabetSection",
    "ProductsSection",
    "TopDealsSection",
    "PromotionSection",
    "StoreSearchSection",
    "NewsletterSection",
    "BreadcrumbSection",
    "Pagination",
    "TagsSection",
    "Footer",
    "BlogSection",
    "CustomBanner",
    "PopularDeals",
    "FooterCategories",
    "CombinedOffersSection",
    "InnerCategoriesSection",
    "SECTION",
  ]);

  if (type === "OfferBannerSection") {
    return (
      <Suspense fallback={null}>
        <OfferBannerSectionComponent component={component} context={context} />
      </Suspense>
    );
  }

  if (component.runtime === "builder-preview-header") {
    return (
      <BuilderHeaderRuntime
        structure={component.structure as ComponentNode | undefined}
        componentStyles={component.styles}
      />
    );
  }

  if (component.runtime === "builder-preview-footer") {
    return (
      <BuilderFooterRuntime
        structure={component.structure as ComponentNode | undefined}
        componentStyles={component.styles}
      />
    );
  }

  if (
    component.type === "OffersSection" &&
    component.runtime === "builder-preview-offers"
  ) {
    return <BuilderOffersRuntime component={component} context={context} />;
  }

  if (type === "EventDealsSection") {
    return (
      <Suspense fallback={null}>
        <EventDealsComponent component={component} context={context} />
      </Suspense>
    );
  }

  if (type === "CategorySection") {
    return (
      <Suspense fallback={null}>
        <CategorySectionComponent component={component} context={context} />
      </Suspense>
    );
  }

  if (
    (component.apiEndpoint || component.apiConfig?.apiEndpoint) &&
    dynamicSectionTypes.has(type)
  ) {
    // Without its own Suspense boundary, this async Server Component has
    // no isolated retry scope: React's RSC renderer re-invokes an
    // un-boundaried async component (re-running its fetch from scratch)
    // every time ANY sibling/ancestor Suspense boundary resolves and
    // retries the still-pending part of the tree. On a page with several
    // async sections (header widgets, footer, other sections), that
    // compounded into dozens of duplicate fetches to the same endpoint
    // for a single request - confirmed via a server-side stack trace
    // showing repeated `retryNode` frames re-entering this component.
    return (
      <Suspense fallback={null}>
        <DynamicSectionComponent component={component} context={context} />
      </Suspense>
    );
  }

  if (
    component.openDetailsModal &&
    (type === "P" || type === "TEXT" || type === "BUTTON" || type === "LINK")
  ) {
    return (
      <ProductDetailTrigger
        component={component}
        data={data}
        context={context}
      />
    );
  }

  switch (type) {
    case "H1":
    case "H2":
    case "H3":
    case "H4":
      return (
        <HeadingComponent component={component} data={data} context={context} />
      );

    case "P":
    case "TEXT":
      return (
        <TextComponent component={component} data={data} context={context} />
      );

    case "BUTTON":
      return (
        <ButtonComponent component={component} data={data} context={context} />
      );

    case "LINK":
      return (
        <LinkComponent component={component} data={data} context={context}>
          {component.components?.length
            ? component.components.map((child) => (
                <ComponentRenderer
                  key={child.id}
                  component={child}
                  data={data}
                  context={context}
                />
              ))
            : undefined}
        </LinkComponent>
      );

    case "ICON":
      return (
        <IconComponent component={component} data={data} context={context} />
      );

    case "IMAGE":
      return (
        <ImageComponent component={component} data={data} context={context} />
      );

    case "CARD":
      return (
        <CardComponent component={component} data={data} context={context} />
      );

    case "CONTAINER":
      return (
        <ContainerComponent
          component={component}
          data={data}
          context={context}
        />
      );

    case "COLUMN":
      return (
        <ColumnComponent component={component} data={data} context={context} />
      );

    case "FAQ_ITEM":
      return <FaqItemComponent component={component} data={data} />;

    case "SEARCH_FORM":
      return <SearchFormComponent component={component} context={context} />;

    case "NEWSLETTER_FORM":
      return (
        <NewsletterFormComponent component={component} context={context} />
      );

    case "CONTACT_FORM":
      return <ContactFormComponent component={component} context={context} />;

    case "Header":
      return (
        <Suspense fallback={null}>
          <HeaderComponent
            component={component}
            data={data}
            context={context}
          />
        </Suspense>
      );

    case "Footer":
      return (
        <Suspense fallback={null}>
          <FooterComponent component={component} context={context} />
        </Suspense>
      );

    case "HeroBanner":
    case "FaqSection":
    case "MerchantSection":
    case "OffersSection":
    case "OfferSection":
    case "EventOfferSection":
    case "EventSection":
    case "MerchantsAlphabetSection":
    case "ProductsSection":
    case "TopDealsSection":
    case "PromotionSection":
    case "StoreSearchSection":
    case "NewsletterSection":
    case "BreadcrumbSection":
    case "PopularDeals":
    case "Pagination":
    case "TagsSection":
    case "CustomBanner":
      // Check for structure property first, then fall back to components
      const sectionContent =
        component.structure?.components || component.components;
      const sectionStyles = component.structure?.styles
        ? convertStylesToInline(component.structure.styles)
        : inlineStyles;

      // Debug logging for TopDealsSection
      if (type === "TopDealsSection") {
        console.log("🔍 TopDealsSection rendering:", {
          hasStructure: !!component.structure,
          structureStyles: component.structure?.styles,
          convertedStyles: sectionStyles,
          childrenCount: sectionContent?.length,
        });
      }

      return (
        <section
          style={sectionStyles}
          className={[
            customClassName,
            type === "NewsletterSection" ? "cb-newsletter-section" : undefined,
          ].filter(Boolean).join(" ")}
        >
          {sectionContent?.map((child: any) => (
            <ComponentRenderer
              key={child.id}
              component={child}
              data={data}
              context={context}
            />
          ))}
        </section>
      );

    case "BlogSection":
      if (component.apiEndpoint || !component.components?.length) {
        return <BlogSectionComponent component={component} context={context} />;
      }
      return (
        <section style={inlineStyles} className={customClassName}>
          {component.components?.map((child) => (
            <ComponentRenderer
              key={child.id}
              component={child}
              data={data}
              context={context}
            />
          ))}
        </section>
      );

    case "ROW":
    case "GRID":
      return (
        <div style={layoutStyles} className={['cb-responsive-row', type === 'GRID' ? 'cb-responsive-grid' : '', customClassName].filter(Boolean).join(' ')}>
          {component.components?.map((child) => (
            <ComponentRenderer
              key={child.id}
              component={child}
              data={data}
              context={context}
            />
          ))}
        </div>
      );

    default:
      // "SECTION" is deliberately NOT its own case above — a SECTION
      // node with an apiConfig already returns early via
      // dynamicSectionTypes (see the `if` block before this switch);
      // one without an apiConfig should still fall through to the
      // structuralSectionTypes check below rather than the bare
      // unexpanded <div> the ROW/GRID case gives it.
      if (structuralSectionTypes.has(type)) {
        const sectionStyles = component.structure?.styles
          ? { ...component.structure.styles, ...styles }
          : styles;
        const sectionComponents: ComponentNode[] | undefined = component.components?.length
          ? component.components
          : component.structure?.components ?? component.components;
        return (
          <section
            style={convertStylesToInline(sectionStyles)}
            className={[customClassName, isResponsiveSidebar({ ...component, styles: sectionStyles }) ? "cb-responsive-sidebar" : undefined].filter(Boolean).join(" ") || undefined}
          >
            {sectionComponents?.map((child) => (
              <ComponentRenderer
                key={child.id}
                component={child}
                data={data}
                context={context}
              />
            ))}
          </section>
        );
      }
      console.warn(`Unknown component type: ${type}`);
      return null;
  }
};

export default ComponentRenderer;
